import React, { useEffect, useState } from 'react';

/**
 * Revolt Desktop updater download button
 * - Resolves the latest executable for a given channel via backend (YAML -> exe)
 * - Optionally shows the resolved version in a badge
 */
export function RevoltDesktopAutoUpdaterDownloadButton({ download }) {
  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState(null);

  const channel = download?.channel || null;
  const showVersionBadge = download?.showVersionBadge !== false;

  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      if (!channel) return;
      setLoading(true);
      setErrorText(null);
      setResolved(null);

      try {
        const response = await fetch(
          `/api/revolt-desktop-updater/resolve?channel=${encodeURIComponent(channel)}`,
          { credentials: 'include' }
        );

        const data = await response.json();
        if (cancelled) return;

        if (!response.ok || !data?.success) {
          setErrorText(data?.error || 'Failed to resolve updater channel');
          return;
        }

        setResolved(data);
      } catch (err) {
        if (cancelled) return;
        setErrorText(err?.message || 'Failed to resolve updater channel');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    resolve();

    return () => {
      cancelled = true;
    };
  }, [channel]);

  const handleClick = () => {
    if (!resolved?.exeUrl) return;
    window.open(resolved.exeUrl, '_blank', 'noopener,noreferrer');
  };

  const versionText = resolved?.version || (loading ? 'Resolving...' : null);
  const isUnavailable = Boolean(errorText) || (!loading && !resolved?.exeUrl);

  return (
    <button
      type="button"
      className="product-download-item"
      onClick={handleClick}
      disabled={!resolved?.exeUrl || loading}
      title={errorText || (channel ? `Download latest from ${channel}` : 'Missing channel')}
    >
      <i className={`fa-solid ${loading ? 'fa-spinner fa-spin' : 'fa-download'}`} aria-hidden></i>
      <span className="product-download-name">{download?.name || 'Untitled'}</span>
      {showVersionBadge && (
        <span
          className={[
            'product-download-version-badge',
            isUnavailable
              ? 'product-download-version-badge--unavailable'
              : 'product-download-version-badge--available',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {versionText || 'Unavailable'}
        </span>
      )}
    </button>
  );
}

